import React, { useEffect, useState } from 'react';
import { Box, Card, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import {
  FinancialMetricsChart,
  ComplianceStatus,
  FundBalances,
  RecentTransactions
} from './components';
import { ReportingEngine } from '../../services/ReportingEngine';
import { 
  DashboardMetrics, 
  DashboardApiResponse, 
  TrendData
} from '../../types/reporting';
import { Icons } from './Icons';
import './styles/styles.css';

interface DashboardProps {
  fiscalSponsorId: string;
}

export const StyledCard = styled(Card)(({ theme }) => ({
  padding: '24px',
  borderRadius: '12px',
  backgroundColor: '#1a2035',
  border: '1px solid rgba(82,153,255,0.1)',
}));

export const StyledMetricValue = styled(Typography)(({ theme }) => ({
  color: '#34d399',
  fontSize: '2rem',
  fontWeight: 'bold',
  marginBottom: '8px',
}));

export const StyledMetricLabel = styled(Typography)(({ theme }) => ({
  color: '#6ee7b7',
  fontSize: '0.875rem',
  textTransform: 'uppercase',
}));

const transformDashboardData = (data: DashboardApiResponse): DashboardMetrics => {
  return {
    trends: {
      revenue_trend: (data.trends || [])
        .filter(trend => trend.type === 'revenue')
        .map((trend: TrendData) => ({
          date: new Date(trend.date),
          amount: trend.amount
        })),
      expense_trend: (data.trends || [])
        .filter(trend => trend.type === 'expense')
        .map((trend: TrendData) => ({
          date: new Date(trend.date),
          amount: trend.amount
        }))
    },
    total_fund_balance: data.total_fund_balance || 0,
    active_funds: data.active_funds || 0,
    average_fund_balance: data.average_fund_balance || 0,
    monthly_deposits: data.monthly_deposits || 0,
    monthly_withdrawals: data.monthly_withdrawals || 0,
    compliance_alerts: data.compliance_alerts || 0,
    upcoming_deadlines: (data.upcoming_deadlines || []).map((deadline: { 
      id?: string; 
      date: string | Date; 
      description: string; 
      type?: string; 
    }) => ({
      id: deadline.id || `deadline-${Date.now()}-${Math.random()}`,
      date: new Date(deadline.date),
      description: deadline.description,
      type: deadline.type || 'default'
    })),
    recent_transactions: (data.recent_transactions || []).map((transaction: {
      id?: string;
      date: string | Date;
      description: string;
      amount: number;
      type?: string;
    }) => ({
      id: transaction.id || `transaction-${Date.now()}-${Math.random()}`,
      date: new Date(transaction.date),
      description: transaction.description,
      amount: transaction.amount,
      type: transaction.type || 'default'
    }))
  };
};

export const SponsFIDashboard: React.FC<DashboardProps> = ({ fiscalSponsorId }) => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  const loadDashboardData = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/dashboard/${fiscalSponsorId}`);
      const data = await response.json();
      setMetrics(transformDashboardData(data));
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [fiscalSponsorId]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <StyledCard>
            <Typography variant="h6" sx={{ mb: 3, color: 'inherit' }}>
              Financial Overview
            </Typography>
            <FinancialMetricsChart 
              data={metrics ? [
                ...metrics.trends.revenue_trend.map(item => ({
                  date: item.date.toISOString(),
                  value: item.amount
                })),
                ...metrics.trends.expense_trend.map(item => ({
                  date: item.date.toISOString(),
                  value: -item.amount // Negative for expenses
                }))
              ] : []}
            />
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <StyledCard>
            <Typography variant="h6" sx={{ mb: 3, color: 'inherit' }}>
              Compliance Status
            </Typography>
            <ComplianceStatus 
              alerts={metrics?.compliance_alerts || 0}
              upcomingDeadlines={metrics?.upcoming_deadlines || []}
            />
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledCard>
            <Typography variant="h6" sx={{ mb: 3, color: 'inherit' }}>
              Fund Balances
            </Typography>
            <FundBalances 
              totalAssets={metrics?.total_fund_balance || 0}
              activeFunds={metrics?.active_funds || 0}
              averageBalance={metrics?.average_fund_balance || 0}
              monthlyActivity={{
                deposits: metrics?.monthly_deposits || 0,
                withdrawals: metrics?.monthly_withdrawals || 0
              }}
            />
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledCard>
            <Typography variant="h6" sx={{ mb: 3, color: 'inherit' }}>
              Recent Transactions
            </Typography>
            <RecentTransactions 
              transactions={metrics?.recent_transactions || []}
              onViewAll={() => {/* Navigate to transactions */}}
            />
          </StyledCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SponsFIDashboard; 